import React, { useMemo, useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { InspectorHeader } from '../InspectorHeader/InspectorHeader';
import IconFilterMailSquare from '@/assets/icons/icon-filter-mail-square.svg?react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LayoutElementV2 } from '@/components/inspector-fields/LayoutElement/LayoutElementV2';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import IconPlus from '@/assets/icons/icon-plus.svg?react';
import NodeStrokeSettings from '@/components/inspector-fields/SliderField/NodeStrokeSettings';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchPagesList } from '@/store/pages/thunks';
import { setActivePanel } from '@/store/assetsPanel/assetsPanelSlice';
import {
  selectPagesList,
  selectCurrentPageId,
  selectIsPagesLoading,
} from '@/store/pages/selectors';
import IconPaintBoard from '@/assets/icons/icon-paint-board.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import {
  DraggableFormFieldList,
  type DraggableFormFieldItem,
} from '@/components/inspector-fields/FormField/DraggableFormFieldList';
import { useEditor, type Node } from '@craftjs/core';
import { FormTextField } from '@/components/inspector/form-fields/TextField/TextField';
import { EmailField } from '@/components/inspector/form-fields/EmailField/EmailField';
import { PhoneField } from '@/components/inspector/form-fields/PhoneField/PhoneField';
import { ConsentField } from '@/components/inspector/form-fields/ConsentField/ConsentField';
import { FormDropdownField } from '@/components/inspector/form-fields/DropdownField/DropdownField';
import { FormSelectionField } from '@/components/inspector/form-fields/SelectionField/SelectionField';
import FormStylesField from '@/components/inspector/form-fields/FormStylesField/FormStylesField';
import { computeFloatingPosition } from '@/utils/computeFloatingPosition';
import ButtonField from '@/components/inspector/form-fields/ButtonField/ButtonField';
import type { FormProps, FormSubmitButtonProps } from '@builder4/shared/components/Form';
import type { ButtonVariant } from '@builder4/shared/styles/constants';
import { FORM_NODE_NAME } from '@/utils/constants';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { useFloatingPanel } from '@/hooks/useFloatingPanel';
import { createPortal } from 'react-dom';
import { isFormControlNode, isFormFieldNode } from '@builder4/shared/components/Form/formRegistry';
import type { FormFieldType } from '@builder4/shared/types/form';
import { InspectorNodeProvider } from '@/context/InspectorNodeContext';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import NodeAnimationSettings from '@/components/inspector-fields/AnimationSettings/NodeAnimationSettings';
import { ASSETS_PANEL, FORM_SUBMIT_BUTTON_NODE_NAME } from '@builder4/shared/utils/constants';
import NodeShadowField from '@/components/inspector-fields/ShadowField/NodeShadowField';

// Module-level ref to persist scroll position across re-mounts
let formInspectorScrollTop = 0;
// Module-level ref to persist Field Styles panel state across re-mounts
let persistedFormStylesPos: { top: number; left: number } | null = null;

interface InspectorFormProps {
  className: string;
  text: string;
}

export const InspectorForm: React.FC<InspectorFormProps> = ({ className }) => {
  const { actions, query } = useEditor((_, q) => ({ query: q }));
  const { duplicateNode } = useCraftHelper();
  const { setBPProp, getBPProp } = useBreakpointAwareProp(FORM_NODE_NAME);

  // Derive the selected Form node and its children fields dynamically
  const { formId, formNode, fieldNodes, submitButtonId, submitButtonNode } = useEditor((_, q) => {
    const selectedId = q.getEvent('selected').first();
    const findFormAncestor = (startId?: string | null): Node | null => {
      if (!startId) return null;
      let cursorId: string | null = startId as string;
      while (cursorId) {
        const n = q.node(cursorId).get();
        if (!n) break;
        if (n.data.displayName === FORM_NODE_NAME) return n;
        const parentId = n.data.parent as string | undefined;
        if (!parentId) break;
        cursorId = parentId;
      }
      return null;
    };

    const form = findFormAncestor(selectedId);
    const fields = form
      ? (form.data.nodes || [])
          .map((cid: string) => q.node(cid).get())
          .filter((n): n is Node => !!n)
          .filter((n: Node) => isFormFieldNode(n.data.displayName))
      : [];
    const submitId = form
      ? (form.data.nodes || []).find((cid: string) => {
          const n = q.node(cid).get();
          return n?.data.displayName && isFormControlNode(n.data.displayName);
        })
      : undefined;
    const submitNode = submitId ? q.node(submitId).get() : null;

    return {
      formId: form?.id as string | undefined,
      formNode: form || null,
      fieldNodes: fields,
      submitButtonId: submitId,
      submitButtonNode: submitNode,
    };
  });

  // Derive button variant from the actual submit button node
  const buttonVariantDefault = useMemo(() => {
    if (!submitButtonNode) return 'primary';
    const submitButtonProps = submitButtonNode.data.props as FormSubmitButtonProps;
    const variant = getBPProp(submitButtonProps, 'variant') as ButtonVariant | undefined;
    return variant || 'primary';
  }, [submitButtonNode, getBPProp]);

  const handleVariantChange = (value: string) => {
    if (!submitButtonId) return;
    actions.setProp(submitButtonId, (props: FormSubmitButtonProps) => {
      setBPProp(props, 'variant', value as ButtonVariant, FORM_SUBMIT_BUTTON_NODE_NAME);
    });
  };

  // Fetch published pages for the landing page dropdown
  const dispatch = useAppDispatch();
  const pages = useAppSelector(selectPagesList);
  const currentPageId = useAppSelector(selectCurrentPageId);
  const isPagesLoading = useAppSelector(selectIsPagesLoading);
  // Filter out pages that are not published (no domain/path) and the current page
  const filteredPages = useMemo(
    () => pages.filter((page) => page.domain && page.path && page.id !== currentPageId),
    [pages, currentPageId],
  );

  // Build URL from page ID
  const constructLinkedPageUrl = (pageId: string) => {
    const page = pages.find((p) => p.id === pageId);
    if (!page || !page.domain || !page.path) return '';
    return `https://${page.domain}/${page.path}`;
  };

  // Get the followup action from props
  const followupAction = useMemo(() => {
    if (!formNode) return undefined;
    const formProps = formNode.data.props as FormProps;
    return getBPProp(formProps, 'followupAction');
  }, [formNode, getBPProp]);

  // Derive initial redirect type from props (used only for initialization)
  const getInitialRedirectType = (): string => {
    if (!followupAction) return 'default';
    switch (followupAction.type) {
      case 'landing':
        return 'landing';
      case 'redirect':
        return 'external';
      case 'remain':
        return 'remain';
      case 'default':
        return 'default';
      default:
        return 'default';
    }
  };

  // Local state for redirect type - controls UI independently after initialization
  const [redirectType, setRedirectType] = useState<string>(getInitialRedirectType());

  // Sync local state when the form node changes (e.g., selecting different form)
  useEffect(() => {
    setRedirectType(getInitialRedirectType());
  }, [formId, followupAction?.type]);

  useEffect(() => {
    dispatch(fetchPagesList());
  }, [dispatch]);

  // Helper to normalize URL (ensure https:// prefix)
  const normalizeUrl = (url: string): string => {
    let value = url.trim();
    if (!value) return '';
    // Remove leading www. if no protocol
    if (value.startsWith('www.')) {
      value = value.slice(4);
    }
    // Add https:// if no protocol
    if (!value.startsWith('http://') && !value.startsWith('https://')) {
      value = `https://${value}`;
    }
    return value;
  };

  // Sanitize external URL on blur (normalize to full URL)
  const handleSanitizeUrl = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!formId) return;
    const normalizedUrl = normalizeUrl(e.target.value);
    actions.setProp(formId, (props: FormProps) => {
      setBPProp(props, 'followupAction', { type: 'redirect', url: normalizedUrl });
    });
  };

  const [fieldTypes, setFieldTypes] = useState<Record<string, FormFieldType>>({});
  const [openFieldPanel, setOpenFieldPanel] = useState<{
    id: string;
    fieldType: FormFieldType;
  } | null>(null);
  const inspectorRef = useRef<HTMLDivElement | null>(null);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const stylesAnchorRef = useRef<HTMLDivElement | null>(null);

  // Preserve scroll position when switching between form elements
  useEffect(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport) return;

    // Restore scroll position on mount
    viewport.scrollTop = formInspectorScrollTop;

    // Save scroll position on scroll
    const handleScroll = () => {
      formInspectorScrollTop = viewport.scrollTop;
    };
    viewport.addEventListener('scroll', handleScroll);
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize formStylesPos from persisted state
  const [formStylesPos, setFormStylesPos] = useState<{ top: number; left: number } | null>(
    persistedFormStylesPos,
  );

  // Sync persisted state when formStylesPos changes
  useEffect(() => {
    persistedFormStylesPos = formStylesPos;
  }, [formStylesPos]);

  const actionEditRef = useRef<HTMLButtonElement | null>(null);
  const [actionButtonPos, setActionButtonPos] = useState<{ top: number; left: number } | null>(
    null,
  );
  const [buttonStyleDetached, setButtonStyleDetached] = useState(false);

  // Use floating panel hook for field panel positioning
  const fieldPanelFloating = useFloatingPanel({
    isOpen: !!openFieldPanel,
    anchorId: openFieldPanel?.id,
    inspectorEl: inspectorRef.current,
    width: 240,
    height: 480,
  });

  // Helper function to detect field type from node's displayName
  const getFieldTypeFromDisplayName = (displayName: string): FormFieldType => {
    const typeMap: Record<string, FormFieldType> = {
      Email: 'email',
      Phone: 'phone',
      Consent: 'consent',
      Dropdown: 'dropdown',
      Selection: 'selection',
    };

    const foundKey = Object.keys(typeMap).find((key) => displayName?.includes(key));
    return foundKey ? typeMap[foundKey] : 'text';
  };

  // Helper function to get default label for a field type
  const getDefaultLabelForFieldType = (type: FormFieldType): string => {
    const typeToDefaultLabel: Record<FormFieldType, string> = {
      email: 'Email',
      text: 'Text',
      phone: 'Phone',
      consent: 'Consent',
      dropdown: 'Dropdown',
      selection: 'Selection',
    };
    const typeBase = typeToDefaultLabel[type];
    const existingFieldLabels = fieldItems
      .filter((item) => item.fieldType === type)
      .map((item) => item.label ?? '');
    const maxNumber = existingFieldLabels
      .map((label) => {
        const rest = label.replace(typeBase, '').trim();
        return /^\d+$/.test(rest) ? parseInt(rest, 10) : 0;
      })
      .reduce((max, n) => Math.max(max, n), 0);
    return `${typeBase} ${maxNumber + 1}`;
  };

  // Helper function to get common properties for a field node
  const getCommonPropertiesForFieldType = (
    props: Record<string, unknown>,
  ): Record<string, unknown> => {
    const showLabel = getBPProp(props, 'showLabel');
    const required = getBPProp(props, 'required');
    const hidden = getBPProp(props, 'hidden');
    const helpText = getBPProp(props, 'helpText');
    const showHelpText = getBPProp(props, 'showHelpText');
    const urlMapping = getBPProp(props, 'urlMapping');
    const commonProps = {
      showLabel: showLabel,
      required: required,
      hidden: hidden,
      helpText: helpText,
      showHelpText: showHelpText,
      urlMapping: urlMapping,
    };

    return commonProps;
  };

  // Sync field types when nodes change
  useEffect(() => {
    setFieldTypes((prev) => {
      const next = { ...prev };
      fieldNodes.forEach((n) => {
        if (!next[n.id]) {
          // Detect field type from node's displayName instead of defaulting to text
          next[n.id] = getFieldTypeFromDisplayName(n.data.displayName);
        }
      });
      // Remove types for deleted nodes
      Object.keys(next).forEach((id) => {
        if (!fieldNodes.find((n) => n.id === id)) delete next[id];
      });
      return next;
    });
    // Close panel if its field disappeared
    setOpenFieldPanel((cur) => (cur && !fieldNodes.find((n) => n.id === cur.id) ? null : cur));
  }, [fieldNodes]);

  const fieldItems: DraggableFormFieldItem[] = useMemo(() => {
    return fieldNodes.map((n) => {
      const fieldProps = n.data.props;
      const label = getBPProp(fieldProps, 'label');
      const isPrimaryEmailField = getBPProp(fieldProps, 'primaryEmailField') === true;

      return {
        id: n.id,
        fieldType: fieldTypes[n.id] || 'text',
        label: label || 'Field',
        isPrimaryEmailField,
      };
    });
  }, [fieldNodes, fieldTypes, getBPProp]);

  const handleReorder = (next: DraggableFormFieldItem[]) => {
    if (!formId) return;

    // Get the new order of IDs from the drag result
    const newOrder = next.map((item) => item.id);

    // Get current field IDs in their current order
    const currentFieldIds = fieldNodes.map((n) => n.id);

    // Check if order actually changed
    if (JSON.stringify(newOrder) === JSON.stringify(currentFieldIds)) {
      return;
    }

    // Get all children of the form (including submit button)
    const freshFormNode = query.node(formId).get();
    if (!freshFormNode) return;

    const allChildren: string[] = freshFormNode.data.nodes || [];

    // Separate field nodes from non-field nodes (like submit button)
    const fieldIdSet = new Set(currentFieldIds);
    const nonFieldNodes = allChildren.filter((id) => !fieldIdSet.has(id));

    // Build new children array: reordered fields + non-field nodes at the end
    const newChildren = [...newOrder, ...nonFieldNodes];

    // Update the form's nodes array directly using setProp workaround
    // Since Form is not a Canvas, we need to manually update the nodes array
    actions.history.throttle().setState((state) => {
      const formNode = state.nodes[formId];
      if (formNode) {
        formNode.data.nodes = newChildren;
      }
    });
  };

  const handleAddField = async () => {
    if (!formId || !formNode) return;

    // Always create a new text field with default properties
    // Dynamically import to avoid circular dependencies
    const module = await import('@/components/userComponents/Form/fields/FormTextField');
    const FormTextFieldComponent = module.default;

    // Create new node tree with default text field props
    const nodeTree = query
      .parseReactElement(<FormTextFieldComponent label={getDefaultLabelForFieldType('text')} />)
      .toNodeTree();

    // Determine where to insert the new field
    let insertIndex: number;
    if (submitButtonId) {
      // Insert before the submit button
      const siblings = formNode.data.nodes || [];
      const submitIdx = siblings.indexOf(submitButtonId);
      insertIndex = submitIdx !== -1 ? submitIdx : siblings.length;
    } else {
      // No submit button, add to the end
      insertIndex = (formNode.data.nodes || []).length;
    }

    // Add the new field to the form
    actions.addNodeTree(nodeTree, formId, insertIndex);
    actions.selectNode(nodeTree.rootNodeId);

    // Update local state to track the new field type
    setFieldTypes((prev) => ({
      ...prev,
      [nodeTree.rootNodeId]: 'text',
    }));
  };

  const handleEditField = (id: string) => {
    if (!id) return;
    const fieldType = fieldTypes[id] || 'text';

    // Open requested field panel and ensure styles / button panels are closed
    setOpenFieldPanel({ id, fieldType });
    setActionButtonPos(null);
    setFormStylesPos(null);
  };

  const handleFieldTypeChange = async (id: string, type: FormFieldType) => {
    if (!formId) return;

    const node = query.node(id).get();
    if (!node) return;

    const currentType = getFieldTypeFromDisplayName(node.data.displayName);
    if (currentType === type) return; // Already the correct type

    // Extract common properties to preserve
    const oldProps = node.data.props;
    const currentLabel = getBPProp(oldProps, 'label');

    const commonProps = getCommonPropertiesForFieldType(oldProps);

    // Preserve label if it's custom, otherwise use default for new type
    const regex = new RegExp(`^${currentType}\\s\\d+$`, 'i');
    commonProps['label'] =
      !currentLabel || regex.test(currentLabel.trim())
        ? getDefaultLabelForFieldType(type)
        : currentLabel;

    const parentId = node.data.parent as string;
    const siblings = formNode?.data.nodes || [];
    const currentIndex = siblings.indexOf(id);

    // Dynamically import the correct field component to avoid circular dependencies
    let Component;
    switch (type) {
      case 'email': {
        const module = await import('@/components/userComponents/Form/fields/FormEmailField');
        Component = module.default;
        break;
      }
      case 'consent': {
        const module = await import('@/components/userComponents/Form/fields/FormConsentField');
        Component = module.default;
        break;
      }
      case 'phone': {
        const module = await import('@/components/userComponents/Form/fields/FormPhoneField');
        Component = module.default;
        break;
      }
      case 'selection': {
        const module = await import('@/components/userComponents/Form/fields/FormSelectionField');
        Component = module.default;
        break;
      }
      case 'dropdown': {
        const module = await import('@/components/userComponents/Form/fields/FormDropdownField');
        Component = module.default;
        break;
      }
      default: {
        // For now, all non-email/consent/selection/dropdown fields default to text field
        const module = await import('@/components/userComponents/Form/fields/FormTextField');
        Component = module.default;
        break;
      }
    }

    // Create new node tree with the correct component type and preserved props
    const nodeTree = query.parseReactElement(<Component {...commonProps} />).toNodeTree();
    // Replace the old node with the new one — merge into a single undo entry
    actions.delete(id);
    actions.history.merge().addNodeTree(nodeTree, parentId, currentIndex);
    actions.selectNode(nodeTree.rootNodeId);

    // Update local state to reflect the new type
    const newId = Object.keys(nodeTree)[0];
    setFieldTypes((prev) => {
      const next = { ...prev };
      delete next[id];
      next[newId] = type;
      return next;
    });
  };

  const handleDuplicateField = (id: string) => {
    if (!id || !formId || !formNode) return;
    const newId = duplicateNode(id);
    if (newId && submitButtonId) {
      // Ensure the new node sits before the submit button if needed
      const siblings = formNode.data.nodes || [];
      const submitIdx = siblings.indexOf(submitButtonId);
      const newIdx = siblings.indexOf(newId);
      if (submitIdx !== -1 && newIdx > submitIdx) {
        actions.move(newId, formId, Math.max(0, submitIdx));
      }
    }
  };

  const handleDeleteField = (id: string) => {
    if (!id || !formId) return;

    // Check if the field being deleted is currently selected/focused
    const selectedNodes = query.getEvent('selected').all();
    const isDeletingSelectedField = selectedNodes.includes(id);

    // Close field panel if we're deleting the currently open field
    setOpenFieldPanel((cur) => (cur && cur.id === id ? null : cur));

    // If we deleted the selected field, select the form to maintain focus
    if (isDeletingSelectedField && formId) {
      actions.selectNode(formId);
    }

    // Delete the node
    actions.delete(id);
  };

  const closeFieldPanel = () => setOpenFieldPanel(null);

  const renderFieldPanel = () => {
    if (!openFieldPanel) return null;
    const commonProps = {
      onClose: closeFieldPanel,
      fieldNodeId: openFieldPanel.id,
      getDefaultFieldLabel: () => getDefaultLabelForFieldType(openFieldPanel.fieldType),
    } as const;
    let node: React.ReactNode;
    switch (openFieldPanel.fieldType) {
      case 'email':
        node = <EmailField key={openFieldPanel.id} {...commonProps} />;
        break;
      case 'phone':
        node = <PhoneField key={openFieldPanel.id} {...commonProps} />;
        break;
      case 'consent':
        node = <ConsentField key={openFieldPanel.id} {...commonProps} />;
        break;
      case 'dropdown':
        node = <FormDropdownField key={openFieldPanel.id} {...commonProps} />;
        break;
      case 'selection':
        node = <FormSelectionField key={openFieldPanel.id} {...commonProps} />;
        break;
      case 'text':
      default:
        node = <FormTextField key={openFieldPanel.id} {...commonProps} />;
    }
    return (
      <div
        ref={fieldPanelFloating.panelRef}
        className="fixed z-[100600]"
        style={{
          top: fieldPanelFloating.position.top,
          left: fieldPanelFloating.position.left,
          visibility: fieldPanelFloating.position.isPositioned ? 'visible' : 'hidden',
        }}
        data-form-field-panel
      >
        {node}
      </div>
    );
  };

  const handleConnectWithWorkflow = () => {
    dispatch(setActivePanel(ASSETS_PANEL.INTEGRATIONS));
  };

  return (
    <InspectorNodeProvider nodeId={formId}>
      <div
        ref={inspectorRef}
        className={clsx(
          'inspector bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] w-[240px] flex-col border-l border-neutral-100',
          className,
        )}
      >
        <InspectorHeader
          className="max-w-[240px] text-neutral-900"
          icon={IconFilterMailSquare}
          text="Form"
        />
        <ScrollArea
          className="h-[calc(100vh-104px)] overflow-hidden"
          viewportRef={scrollViewportRef}
        >
          <div className="flex w-full flex-col gap-3 p-3">
            {/* Layout (unified component) - visibleLayout=false to prevent scroll jumping */}
            <LayoutElementV2
              className="max-w-[216px]"
              state="default"
              visibleLayout={false}
              data-qa-selector="inspector-form-layout-element"
            />
            {/* Divider */}
            <div className="my-1 h-0 w-full max-w-[216px] self-stretch border-t border-neutral-100" />
            {/* Fields */}
            <div className="flex w-full max-w-[216px] flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-neutral-900">Fields</div>
                <div ref={stylesAnchorRef}>
                  <IconSimpleButton
                    icon={IconPaintBoard}
                    size={24}
                    noBgWhenDefault
                    tooltipShow
                    tooltipContent={formStylesPos ? 'Close Styles' : 'Styles'}
                    onClick={() => {
                      if (formStylesPos) {
                        setFormStylesPos(null);
                        return;
                      }
                      const inspectorEl = stylesAnchorRef.current?.closest(
                        '.inspector',
                      ) as HTMLElement | null;
                      const p = inspectorEl
                        ? computeFloatingPosition({
                            inspectorEl,
                            anchorEl: stylesAnchorRef.current,
                            width: 240,
                            height: 560,
                            preferSide: 'left',
                          })
                        : null;
                      if (p) {
                        // Open styles panel and ensure field / button panels are closed
                        setFormStylesPos({ top: p.top, left: p.left });
                        setOpenFieldPanel(null);
                        setActionButtonPos(null);
                      }
                    }}
                    data-qa-selector="inspector-form-field-styles-button"
                  />
                </div>
              </div>
              <DraggableFormFieldList
                items={fieldItems}
                onReorder={handleReorder}
                onEdit={(id) => id && handleEditField(id)}
                onFieldTypeChange={(id, type) => handleFieldTypeChange(id, type)}
                onDuplicate={(id) => id && handleDuplicateField(id)}
                onDelete={(id) => id && handleDeleteField(id)}
                data-qa-selector="inspector-form-field-list"
              />
              <div className="flex items-center justify-between">
                <div className="text-button-sm font-medium text-neutral-500">New Field</div>
                <SecondaryButton
                  size="sm"
                  icon={IconPlus}
                  text="Add Field"
                  onClick={handleAddField}
                  data-qa-selector="inspector-form-add-field-button"
                />
              </div>
            </div>
            <div className="my-1 h-0 w-full max-w-[216px] self-stretch border-t border-neutral-100" />
            {/* Integrations */}
            <div className="flex w-full max-w-[216px] flex-col gap-3">
              <div className="text-xs font-semibold text-neutral-900">Integrations</div>
              <div className="text-xs text-neutral-500">
                Route your form leads to any integration.
              </div>
              <SecondaryButton
                text="Send to integration"
                size="sm"
                icon={null}
                state="default"
                onClick={handleConnectWithWorkflow}
                data-qa-selector="inspector-form-connect-workflow-button"
              />
            </div>
            <div className="my-1 h-0 w-full max-w-[216px] self-stretch border-t border-neutral-100" />
            <div className="flex w-full max-w-[216px] flex-col gap-3">
              <div className="text-xs font-semibold text-neutral-900">Actions</div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  <SelectField
                    text="Button"
                    size="small"
                    defaultValue={buttonVariantDefault}
                    onValueChange={handleVariantChange}
                    data-qa-selector="inspector-form-button-variant"
                  >
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="accent">Accent</SelectItem>
                  </SelectField>
                </div>
                <SecondaryButton
                  size="sm"
                  text="Edit"
                  icon={null}
                  ref={actionEditRef as React.RefObject<HTMLButtonElement>}
                  onClick={() => {
                    if (actionButtonPos) {
                      // keep text Edit; just close panel
                      setActionButtonPos(null);
                      return;
                    }
                    const inspectorEl = document.querySelector('.inspector') as HTMLElement | null;
                    const anchorEl = actionEditRef.current;
                    const p = inspectorEl
                      ? computeFloatingPosition({
                          inspectorEl,
                          anchorEl,
                          width: 240,
                          height: 480,
                          preferSide: 'left',
                        })
                      : null;
                    if (p) {
                      // Open button panel and ensure field / styles panels are closed
                      setActionButtonPos({ top: p.top, left: p.left });
                      setOpenFieldPanel(null);
                      setFormStylesPos(null);
                    }
                  }}
                  data-qa-selector="inspector-form-edit-button"
                />
              </div>

              <SelectField
                key={redirectType}
                text="Redirect"
                size="medium"
                truncate={false}
                showInfoIcon
                infoTooltipText="Where visitors go after submitting the form. 'Remain on page' keeps them here. 'Default page' or 'Landing page' sends them to another page of your choice. 'External URL' sends them to any web address you choose."
                defaultValue={redirectType}
                onValueChange={(val) => {
                  // Update local state immediately for UI responsiveness
                  setRedirectType(val);

                  if (!formId) return;
                  actions.setProp(
                    formId,
                    (props: { followupAction?: FormProps['followupAction'] }) => {
                      switch (val) {
                        case 'default':
                          setBPProp(props, 'followupAction', { type: 'default' });
                          break;
                        case 'landing': {
                          // Set first available page URL and ID as default for landing type
                          const firstPage = filteredPages[0];
                          const url = firstPage ? constructLinkedPageUrl(firstPage.id) : '';
                          setBPProp(props, 'followupAction', {
                            type: 'landing',
                            url,
                            pageId: firstPage?.id,
                          });
                          break;
                        }
                        case 'external':
                          setBPProp(props, 'followupAction', {
                            type: 'redirect',
                            url: '',
                          });
                          break;
                        case 'remain':
                          setBPProp(props, 'followupAction', { type: 'remain' });
                          break;
                        default:
                          setBPProp(props, 'followupAction', { type: 'remain' });
                      }
                    },
                  );
                }}
                data-qa-selector="inspector-form-redirect"
              >
                <SelectItem value="default">Default page</SelectItem>
                <SelectItem value="landing">Landing page</SelectItem>
                <SelectItem value="external">External URL</SelectItem>
                <SelectItem value="remain">Remain on page</SelectItem>
              </SelectField>

              {/* Conditionally show URL input for External URL */}
              {redirectType === 'external' && (
                <TextField
                  className=""
                  classNameInputParent=""
                  classNameInput="max-w-[140px] w-full"
                  text="URL"
                  inputValue={followupAction?.url || ''}
                  placeholderText="Enter URL"
                  onChange={(e) => {
                    if (!formId) return;
                    actions.setProp(formId, (props: FormProps) => {
                      setBPProp(props, 'followupAction', {
                        type: 'redirect',
                        url: e.target.value,
                      });
                    });
                  }}
                  onBlur={handleSanitizeUrl}
                  data-qa-selector="inspector-form-redirect-url"
                />
              )}

              {/* Conditionally show Page dropdown for Landing page */}
              {redirectType === 'landing' && (
                <SelectField
                  text="Page"
                  size="medium"
                  defaultValue={
                    followupAction?.type === 'landing'
                      ? followupAction.pageId
                      : filteredPages[0]?.id || ''
                  }
                  onValueChange={(pageId) => {
                    if (!formId) return;
                    const url = constructLinkedPageUrl(pageId);
                    actions.setProp(formId, (props: FormProps) => {
                      setBPProp(props, 'followupAction', {
                        type: 'landing',
                        url,
                        pageId,
                      });
                    });
                  }}
                  data-qa-selector="inspector-form-redirect-page"
                >
                  {isPagesLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading pages...
                    </SelectItem>
                  ) : filteredPages.length === 0 ? (
                    <SelectItem value="no-pages" disabled>
                      No pages available
                    </SelectItem>
                  ) : (
                    filteredPages.map((page) => (
                      <SelectItem key={page.id} value={page.id}>
                        {page.name || `Page ${page.id.slice(0, 8)}`}
                      </SelectItem>
                    ))
                  )}
                </SelectField>
              )}

              <TextField
                className=""
                classNameInputParent="overflow-x-auto"
                classNameInput="max-w-[140px] w-full"
                text="Form ID"
                inputValue={String(
                  formNode
                    ? (getBPProp(formNode.data.props as FormProps, 'id') as string | undefined) ||
                        '-'
                    : '-',
                )}
                placeholderText=""
                readOnly
                data-qa-selector="inspector-form-id"
              />
            </div>
            <div className="my-1 h-0 w-full max-w-[216px] self-stretch border-t border-neutral-100" />
            {/* Appearance */}
            <div className="flex w-full max-w-[216px] flex-col gap-3">
              <div className="text-xs font-semibold text-neutral-900">Appearance</div>

              <NodeColorField
                propertyName="backgroundColor"
                solidPropertyName="bgSolid"
                gradientPropertyName="bgGradient"
                gradientEnabled
                label="Background"
                data-qa-selector="inspector-form-background"
              />

              <NodeColorField
                propertyName="borderColor"
                ensureStrokeVisibleProp="borderWidth"
                label="Stroke Color"
                data-qa-selector="inspector-form-stroke-color"
              />

              <NodeColorField
                propertyName="hoverBorderColor"
                ensureStrokeVisibleProp="borderWidth"
                label="Hover Stroke"
                data-qa-selector="inspector-form-hover-stroke"
              />

              <NodeStrokeSettings data-qa-selector="inspector-form-stroke" />

              <NodeShadowField data-qa-selector="inspector-form-shadow" />
            </div>
            <div className="my-1 h-0 w-full max-w-[216px] self-stretch border-t border-neutral-100" />
            <NodeAnimationSettings />
          </div>
          {openFieldPanel && renderFieldPanel()}
        </ScrollArea>
        {formStylesPos &&
          createPortal(
            <FormStylesField
              outside
              floatingPosition={formStylesPos}
              formId={formId}
              onClose={() => setFormStylesPos(null)}
            />,
            document.body,
          )}
        {actionButtonPos &&
          createPortal(
            <div
              className="fixed z-[100600]"
              style={{ top: actionButtonPos.top, left: actionButtonPos.left }}
              data-action-button-panel
            >
              <ButtonField
                onClose={() => setActionButtonPos(null)}
                styleDetached={buttonStyleDetached}
                onToggleStyle={() => setButtonStyleDetached((v) => !v)}
                submitButtonId={submitButtonId}
              />
            </div>,
            document.body,
          )}
      </div>
    </InspectorNodeProvider>
  );
};

export default InspectorForm;
